// @ts-nocheck
/**
 * Modern Filters System
 * Handles range sliders and modern filter interactions
 * Uses full page reload instead of section rendering for stability
 */

import { Component } from "@theme/component";

/**
 * Modern Range Slider Filter Component
 */
class ModernRangeSlider extends Component {
  requiredRefs = ["sliderContainer", "minValue", "maxValue"];

  /** @type {any | null} */
  slider = null;

  /** @type {number} */
  min = 0;

  /** @type {number} */
  max = 10;

  /** @type {string} */
  filterParam = "";

  /** @type {string} */
  unit = "";

  /** @type {boolean} */
  isPrice = false;

  connectedCallback() {
    super.connectedCallback();

    this.#parseConfig();
    this.#loadNoUiSlider();
  }

  /**
   * Called when the component is updated by Section Rendering API
   */
  updatedCallback() {
    super.updatedCallback();

    // Re-initialize slider after section rendering
    // Add small delay to ensure DOM is fully updated
    setTimeout(() => {
      this.#cleanup();
      this.#parseConfig();
      this.#loadNoUiSlider();
    }, 50);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  /**
   * Parse configuration from data attributes
   */
  #parseConfig() {
    this.min = parseInt(this.dataset.min || "0", 10);
    this.max = parseInt(this.dataset.max || "10", 10);
    this.filterParam = this.dataset.filterParam || "";
    this.unit = this.dataset.unit || "";
    this.isPrice = this.dataset.isPrice === "true";
    this.currencyCode = this.dataset.currency || "EUR";
    this.isSynthetic = this.dataset.synthetic === "true";

    // Get current values from URL
    const urlParams = new URLSearchParams(window.location.search);

    if (this.isPrice && this.isSynthetic) {
      // Synthetic price filter: read from custom URL params
      const minPrice = urlParams.get("synth_price_min");
      const maxPrice = urlParams.get("synth_price_max");

      this.selectedMin = minPrice ? Number(minPrice) : this.min / 100;
      this.selectedMax = maxPrice ? Number(maxPrice) : this.max / 100;
    } else if (this.isPrice) {
      const minPrice = urlParams.get("filter.v.price.gte");
      const maxPrice = urlParams.get("filter.v.price.lte");

      this.selectedMin = minPrice
        ? this.#parseFormattedPrice(minPrice)
        : this.min / 100;
      this.selectedMax = maxPrice
        ? this.#parseFormattedPrice(maxPrice)
        : this.max / 100;
    } else {
      // For list filters (Weeks from metafield list, etc.)
      const activeFilters = urlParams.getAll(this.filterParam);

      if (activeFilters.length > 0) {
        // Extract numeric values - handle both direct numbers and formatted strings
        const numericValues = activeFilters
          .map((val) => {
            // Try to parse as direct integer first
            const directParse = parseInt(val, 10);
            if (!isNaN(directParse)) {
              return directParse;
            }
            // Otherwise extract from string like "4 Weeks"
            const match = val.match(/\d+/);
            return match ? parseInt(match[0], 10) : null;
          })
          .filter((val) => val !== null);

        if (numericValues.length > 0) {
          this.selectedMin = Math.min(...numericValues);
          this.selectedMax = Math.max(...numericValues);
        } else {
          this.selectedMin = this.min;
          this.selectedMax = this.max;
        }
      } else {
        this.selectedMin = this.min;
        this.selectedMax = this.max;
      }
    }
  }

  /**
   * Parse formatted price from URL (e.g., "12.570,00" or "12570" → 12570)
   * @param {string} formattedPrice - The formatted price string
   * @returns {number} The numeric price value
   */
  #parseFormattedPrice(formattedPrice) {
    // Detect format: if contains comma followed by exactly 2 digits at end → comma is decimal
    // "12570,00" → 12570, "12.570,00" → 12570, "12570.00" → 12570, "12,570.00" → 12570
    let cleaned = formattedPrice.trim();
    if (/,\d{2}$/.test(cleaned)) {
      // Comma-decimal format (e.g., "12.570,00" or "12570,00")
      cleaned = cleaned.replace(/\./g, "").replace(/,\d+$/, "");
    } else if (/\.\d{2}$/.test(cleaned)) {
      // Dot-decimal format (e.g., "12,570.00" or "12570.00")
      cleaned = cleaned.replace(/,/g, "").replace(/\.\d+$/, "");
    } else {
      // No decimal, just remove separators
      cleaned = cleaned.replace(/[.,]/g, "");
    }
    return parseInt(cleaned, 10) || 0;
  }

  /**
   * Load noUiSlider library and initialize
   */
  async #loadNoUiSlider() {
    // Check if noUiSlider is already loaded
    if (window.noUiSlider) {
      this.#initSlider();
      return;
    }

    // Wait for library to load (loaded via <script> tag in theme)
    let attempts = 0;
    const maxAttempts = 50;

    const checkInterval = setInterval(() => {
      attempts++;
      if (window.noUiSlider) {
        clearInterval(checkInterval);
        this.#initSlider();
      } else if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
        console.error("noUiSlider failed to load");
      }
    }, 100);
  }

  /**
   * Initialize the slider
   */
  #initSlider() {
    const { sliderContainer } = this.refs;

    // Check if container exists and is a single element (not array)
    if (
      !sliderContainer ||
      Array.isArray(sliderContainer) ||
      !window.noUiSlider
    ) {
      console.warn("Slider container not found or invalid");
      return;
    }

    try {
      this.slider = window.noUiSlider.create(sliderContainer, {
        start: [this.selectedMin, this.selectedMax],
        connect: true,
        step: this.isPrice ? 100 : 1, // Step of 100 = 100€ increment
        range: {
          min: this.isPrice ? this.min / 100 : this.min, // Convert cents to euros
          max: this.isPrice ? this.max / 100 : this.max,
        },
        tooltips: [
          {
            to: (value) => {
              const val = Math.round(value);
              if (this.isPrice) {
                return new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: this.currencyCode,
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(val);
              }
              return `${val} ${this.unit}`;
            },
          },
          {
            to: (value) => {
              const val = Math.round(value);
              if (this.isPrice) {
                return new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: this.currencyCode,
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(val);
              }
              return `${val} ${this.unit}`;
            },
          },
        ],
        format: {
          to: (value) => Math.round(value),
          from: (value) => Number(value),
        },
      });

      // Update display on slider change
      this.slider.on("update", (values) => {
        this.#updateDisplay(values[0], values[1]);
        this.#updateHiddenInputs(values[0], values[1]);
      });

      // Don't submit on change - wait for Apply button
      // this.slider.on("change", () => {
      //   this.#submitForm();
      // });

      // Add ARIA attributes
      this.#setupAccessibility();
    } catch (error) {
      console.error("Error creating slider:", error);
    }
  }

  /**
   * Update display values
   */
  #updateDisplay(min, max) {
    const { minValue, maxValue } = this.refs;

    if (this.isPrice) {
      const minFormatted = Math.round(min);
      const maxFormatted = Math.round(max);
      if (minValue)
        minValue.textContent = new Intl.NumberFormat("en-US", {
          style: "decimal",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(minFormatted);
      if (maxValue)
        maxValue.textContent = new Intl.NumberFormat("en-US", {
          style: "decimal",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(maxFormatted);
    } else {
      if (minValue) minValue.textContent = min;
      if (maxValue) maxValue.textContent = max;
    }
  }

  /**
   * Update hidden inputs for form submission
   */
  #updateHiddenInputs(min, max) {
    const minRounded = Math.round(min);
    const maxRounded = Math.round(max);

    // Find or create hidden input container
    let container = this.querySelector(".modern-range__inputs");
    if (!container) {
      container = document.createElement("div");
      container.className = "modern-range__inputs";
      container.style.display = "none";
      this.appendChild(container);
    }

    // Clear existing inputs
    container.innerHTML = "";

    if (this.isPrice && this.isSynthetic) {
      // Synthetic: use custom param names (Shopify ignores these, handled client-side)
      const priceMin = this.min / 100;
      const priceMax = this.max / 100;
      const isDefaultRange = minRounded === priceMin && maxRounded === priceMax;

      if (!isDefaultRange) {
        const inputMin = document.createElement("input");
        inputMin.type = "hidden";
        inputMin.name = "synth_price_min";
        inputMin.value = String(minRounded);
        container.appendChild(inputMin);

        const inputMax = document.createElement("input");
        inputMax.type = "hidden";
        inputMax.name = "synth_price_max";
        inputMax.value = String(maxRounded);
        container.appendChild(inputMax);
      }
    } else if (this.isPrice) {
      // Native price filter: server-side
      const priceMin = this.min / 100;
      const priceMax = this.max / 100;

      const isDefaultRange = minRounded === priceMin && maxRounded === priceMax;

      if (!isDefaultRange) {
        const inputMin = document.createElement("input");
        inputMin.type = "hidden";
        inputMin.name = "filter.v.price.gte";
        inputMin.value = this.#formatPriceForURL(minRounded);
        container.appendChild(inputMin);

        const inputMax = document.createElement("input");
        inputMax.type = "hidden";
        inputMax.name = "filter.v.price.lte";
        inputMax.value = this.#formatPriceForURL(maxRounded);
        container.appendChild(inputMax);
      }
    } else {
      // For list filters (Weeks metafield list): add individual numeric values
      // Only add filter if user has changed from default range
      const isDefaultRange = minRounded === this.min && maxRounded === this.max;

      if (!isDefaultRange) {
        // The filter expects direct integer values (e.g., 2, 3, 4) not "2 Weeks"
        for (let i = minRounded; i <= maxRounded; i++) {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = this.filterParam;
          input.value = String(i); // Send as numeric string (e.g., "4" not "4 Weeks")
          container.appendChild(input);
        }
      }
    }
  }

  /**
   * Format price value for URL (e.g., 12570 → "12570.00")
   * @param {number} price - The price in whole currency units
   * @returns {string} The formatted price string
   */
  #formatPriceForURL(price) {
    // Plain number with no thousands separator, dot as decimal
    // Shopify accepts this format regardless of store locale
    return price.toFixed(2);
  }

  /**
   * Submit the form with new filter values
   */
  #submitForm() {
    if (!this.slider) return;

    const values = this.slider.get();
    const min = Math.round(values[0]);
    const max = Math.round(values[1]);

    // Build new URL with filter parameters
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);

    if (this.isPrice && this.isSynthetic) {
      // Synthetic price filter: client-side only, encode in URL hash
      params.delete("synth_price_min");
      params.delete("synth_price_max");
      const priceMin = this.min / 100;
      const priceMax = this.max / 100;
      const isDefault = min === priceMin && max === priceMax;
      if (!isDefault) {
        params.set("synth_price_min", String(min));
        params.set("synth_price_max", String(max));
      }
    } else if (this.isPrice) {
      // Native price filter: server-side
      params.delete("filter.v.price.gte");
      params.delete("filter.v.price.lte");

      params.set("filter.v.price.gte", this.#formatPriceForURL(min));
      params.set("filter.v.price.lte", this.#formatPriceForURL(max));
    } else {
      // For list filters (Weeks metafield list), remove existing and add range
      const allParams = Array.from(params.keys());
      allParams.forEach((key) => {
        if (key === this.filterParam) {
          params.delete(key);
        }
      });

      // Add new filter values as numeric strings (e.g., "4" not "4 Weeks")
      for (let i = min; i <= max; i++) {
        params.append(this.filterParam, String(i));
      }
    }

    // Remove page parameter (reset to page 1)
    params.delete("page");

    // Navigate to new URL (full page reload)
    url.search = params.toString();
    window.location.href = url.toString();
  }

  /**
   * Setup accessibility attributes
   */
  #setupAccessibility() {
    if (!this.slider || !this.refs.sliderContainer) return;

    const handles = this.refs.sliderContainer.querySelectorAll(".noUi-handle");
    if (handles[0]) {
      handles[0].setAttribute("role", "slider");
      handles[0].setAttribute("aria-label", `Minimum ${this.unit}`);
      handles[0].setAttribute("aria-valuemin", String(this.min));
      handles[0].setAttribute("aria-valuemax", String(this.max));
      handles[0].setAttribute("tabindex", "0");
    }
    if (handles[1]) {
      handles[1].setAttribute("role", "slider");
      handles[1].setAttribute("aria-label", `Maximum ${this.unit}`);
      handles[1].setAttribute("aria-valuemin", String(this.min));
      handles[1].setAttribute("aria-valuemax", String(this.max));
      handles[1].setAttribute("tabindex", "0");
    }

    // Update aria-valuenow on change
    this.slider.on("update", (values) => {
      if (handles[0])
        handles[0].setAttribute("aria-valuenow", String(values[0]));
      if (handles[1])
        handles[1].setAttribute("aria-valuenow", String(values[1]));
    });
  }

  /**
   * Cleanup slider instance
   */
  #cleanup() {
    if (this.slider && typeof this.slider.destroy === "function") {
      try {
        this.slider.destroy();
      } catch (error) {
        console.warn("Error destroying slider:", error);
      }
      this.slider = null;
    }
  }
}

// Register component
if (!customElements.get("modern-range-slider")) {
  customElements.define("modern-range-slider", ModernRangeSlider);
}

/**
 * Client-side price filtering for synthetic filter (Markets without price_range).
 * Reads synth_price_min/max from URL and hides product cards outside range.
 */
function applySyntheticPriceFilter() {
  const params = new URLSearchParams(window.location.search);
  const minStr = params.get("synth_price_min");
  const maxStr = params.get("synth_price_max");

  if (!minStr && !maxStr) return;

  const min = minStr ? Number(minStr) * 100 : 0;
  const max = maxStr ? Number(maxStr) * 100 : Infinity;

  document.querySelectorAll(".product-grid__item[data-price]").forEach((el) => {
    const price = Number(el.dataset.price);
    if (price < min || price > max) {
      el.style.display = "none";
    }
  });

  // Update product count display
  const visible = document.querySelectorAll(
    '.product-grid__item[data-price]:not([style*="display: none"])',
  ).length;
  const countEl = document.querySelector(".collection-product-count");
  if (countEl) {
    countEl.textContent = `${visible} ${visible === 1 ? "item" : "items"}`;
  }
}

// Run on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", applySyntheticPriceFilter);
} else {
  applySyntheticPriceFilter();
}

export default ModernRangeSlider;
